import { memo, useCallback, useMemo, useState } from 'react';

import { Button } from '@air/primitive-button';

import { DEFAULT_AMOUNT_OF_CUSTOM_FIELDS_TO_SHOW } from '~/components/AssetModalV2/constants';
import { useCollapsibleSectionsContext } from '~/components/AssetModalV2/InfoPanel/CollapsibleSectionsContextProvider';
import { CUSTOM_FIELDS_SECTION } from '~/components/AssetModalV2/InfoPanel/CustomFields/PrivateCustomFieldsCollapsibleSection';
import { CollapsibleSection } from '~/components/AssetModalV2/InfoPanel/components/CollapsibleSection';
import { AssetCustomFieldsPanelContent } from '~/components/RightPanel/CustomFields/AssetCustomFieldsPanelContent';
import { PublicAssetPanelCustomFieldDateInput } from '~/components/RightPanel/CustomFields/PublicAssetPanelCustomFieldDateInput';
import { PublicAssetPanelCustomFieldPlainText } from '~/components/RightPanel/CustomFields/PublicAssetPanelCustomFieldPlainText';
import { PublicAssetPanelCustomFieldSelect } from '~/components/RightPanel/CustomFields/PublicAssetPanelCustomFieldSelect';
import { usePublicAssetCustomFieldsData } from '~/hooks/usePublicAssetCustomFieldsData';

export const PublicCustomFieldsCollapsibleSection = memo(() => {
  const { data: customFields } = usePublicAssetCustomFieldsData();
  const { expandedSections, toggleSection } = useCollapsibleSectionsContext();
  const isExpanded = expandedSections.customFields;
  const [showRestOfFields, setShowRestOfFields] = useState(false);
  const firstThreeFields = customFields?.data.slice(0, DEFAULT_AMOUNT_OF_CUSTOM_FIELDS_TO_SHOW);
  const restOfFields = customFields?.data.slice(DEFAULT_AMOUNT_OF_CUSTOM_FIELDS_TO_SHOW);
  const firstThreeFieldsRender = useMemo(() => {
    return (
      <AssetCustomFieldsPanelContent
        fields={firstThreeFields}
        SelectCustomField={PublicAssetPanelCustomFieldSelect}
        DateCustomField={PublicAssetPanelCustomFieldDateInput}
        PlainTextCustomField={PublicAssetPanelCustomFieldPlainText}
      />
    );
  }, [firstThreeFields]);

  const restOfFieldsRender = useMemo(() => {
    return (
      <AssetCustomFieldsPanelContent
        fields={restOfFields}
        SelectCustomField={PublicAssetPanelCustomFieldSelect}
        DateCustomField={PublicAssetPanelCustomFieldDateInput}
        PlainTextCustomField={PublicAssetPanelCustomFieldPlainText}
      />
    );
  }, [restOfFields]);

  const handleOnClick = useCallback(() => {
    setShowRestOfFields((prev) => !prev);
  }, []);

  const onOpenChange = useCallback(() => {
    toggleSection('customFields');
  }, [toggleSection]);

  return (
    <CollapsibleSection
      title="Custom fields"
      isExpanded={isExpanded}
      onOpenChange={onOpenChange}
      data-testid={CUSTOM_FIELDS_SECTION}
    >
      <div>{firstThreeFieldsRender}</div>
      {!!restOfFields?.length && (
        <Button
          onClick={handleOnClick}
          size="small"
          appearance="outline"
          color="grey"
          data-testid="CUSTOM_FIELDS_TOGGLE_BUTTON"
        >
          <span>{showRestOfFields ? 'Hide All Fields' : 'Show All Fields'}</span>
        </Button>
      )}
      {showRestOfFields && !!restOfFields?.length && <div>{restOfFieldsRender}</div>}
    </CollapsibleSection>
  );
});

PublicCustomFieldsCollapsibleSection.displayName = 'PublicCustomFieldsCollapsibleSection';
